"use client";

import Link from "next/link";
import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { useClientSidebar } from "@/components/sidebar/client-sidebar-context";
import { MobileNavDrawer } from "@/components/sidebar/mobile-nav-drawer";
import { publishingApprovalsPath } from "@/lib/publishing/paths";
import { pendingApprovalsCountAction } from "@/lib/actions/approvals-count";
import { AccountMenu } from "./account-menu";
import { CreateMenu } from "./create-menu";
import { TopBarClientSwitcher } from "./client-switcher";
import { DemoToggle } from "./demo-toggle";
import { GlobalSearch } from "./search/global-search";
import { ClientModeToggle } from "./client-mode-toggle";
import { HelpCenter } from "./help-center";
import { WorkspaceTools } from "./workspace-tools";
import { BrandMark } from "@/components/ui/brand-mark";
import type { ResolvedClientMode } from "@/lib/auth/client-mode-shared";

const STAFF_ROLES = new Set(["developer", "admin", "manager"]);

interface TopBarProps {
  role: string;
  name: string | null;
  email: string;
  clientModeEligible: boolean;
  clientMode: ResolvedClientMode | null;
  signOutAction: () => Promise<void>;
  mobileNav: ReactNode;
}

/**
 * Global header. A light surface shared with the labeled navigation. Everything that is not navigation lives here:
 * the company switcher, demo mode, help, and the account menu (2026-08-01).
 *
 * In flow rather than fixed - the rail below is sticky under it, so content
 * needs no top padding and nothing shifts on load.
 */
export function TopBar({ role, name, email, clientModeEligible, clientMode, signOutAction, mobileNav }: TopBarProps) {
  const { client, collapsed, demoMode, mobileNavOpen, setMobileNavOpen } = useClientSidebar();
  const isStaff = STAFF_ROLES.has(role);
  const [agencyApprovals, setAgencyApprovals] = useState(0);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement>(null);

  // Inside a company the client layout already supplies the count. Outside one
  // there was no signal at all, so staff had to go looking; fetch the
  // cross-company figure only on the pages that need it.
  const needsAgencyCount = isStaff && !client && !demoMode;
  useEffect(() => {
    if (!needsAgencyCount) return;
    let cancelled = false;
    void pendingApprovalsCountAction()
      .then((count) => {
        if (!cancelled) setAgencyApprovals(count);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [needsAgencyCount]);

  const pendingApprovals = client ? (client.publishingPendingCount ?? 0) : agencyApprovals;
  const approvalsHref = client
    ? publishingApprovalsPath(client.clientId)
    : "/agency/social-approvals";

  return (
    <>
    <header className="spark-navigation-surface sticky top-0 z-50 flex h-[var(--topbar-h)] shrink-0 items-center bg-spark-navigation print:hidden">
      <button
        ref={mobileMenuTriggerRef}
        type="button"
        onClick={() => setMobileNavOpen(true)}
        aria-expanded={mobileNavOpen}
        aria-controls="mobile-navigation-drawer"
        aria-label="Open navigation"
        className="relative ml-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-spark-navigation-text transition-colors hover:bg-spark-navigation-hover hover:text-spark-navigation-strong lg:hidden"
      >
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="18" x2="20" y2="18" />
        </svg>
        {pendingApprovals > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-spark-green px-1 text-[10px] font-bold text-spark-ink">
            {pendingApprovals > 9 ? "9+" : pendingApprovals}
          </span>
        )}
      </button>

      {/* Brand block tracks the rail width so the bar and rail line up as one frame */}
      <Link
        href="/"
        title="SPARK Dash"
        className={`hidden h-full shrink-0 items-center gap-2 transition-all duration-200 lg:flex ${
          collapsed ? "w-14 justify-center" : "w-56 px-4"
        }`}
      >
        <BrandMark tone="navigation" size="md" wordmark={!collapsed} />
      </Link>

      <div className="min-w-0 flex-1 lg:hidden">
        <Suspense fallback={<div className="h-11" />}>
          <TopBarClientSwitcher variant="mobile" />
        </Suspense>
      </div>

      {/* useSearchParams: search carries range/compare onto company hrefs */}
      <div className="flex w-11 shrink-0 lg:w-auto lg:min-w-0 lg:flex-1 lg:px-3">
        <Suspense fallback={<div className="h-8 w-full max-w-xl" />}>
          <GlobalSearch role={role} />
        </Suspense>
      </div>


      <div className="hidden shrink-0 items-center gap-1 px-3 lg:flex">
        {isStaff && <CreateMenu />}
        {(isStaff || clientModeEligible) && (
          <WorkspaceTools modeLabel={demoMode ? "Demo" : clientMode ? "Client preview" : undefined}>
            {isStaff && <DemoToggle />}
            {clientModeEligible && <ClientModeToggle mode={clientMode} />}
          </WorkspaceTools>
        )}

        <HelpCenter name={name} role={role} />

        {pendingApprovals > 0 && (
          <Link
            href={approvalsHref}
            title={`${pendingApprovals} awaiting your approval`}
            aria-label={`${pendingApprovals} items awaiting your approval`}
            className="relative flex h-8 w-8 items-center justify-center rounded-md text-spark-navigation-text transition-colors hover:bg-spark-navigation-hover hover:text-spark-navigation-strong"
          >
            <svg
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-spark-green px-1 text-[11px] font-bold tabular-nums text-spark-ink">
              {pendingApprovals > 9 ? "9+" : pendingApprovals}
            </span>
          </Link>
        )}

        {/* useSearchParams needs a boundary above it; the switcher reads range/compare
            so a company jump keeps the date range you were looking at. */}
        <Suspense fallback={<div className="h-8 w-32" />}>
          <TopBarClientSwitcher />
        </Suspense>

        <AccountMenu name={name} email={email} role={role} signOutAction={signOutAction} />
      </div>

      <div className="mr-2 flex h-11 w-11 shrink-0 items-center justify-center lg:hidden">
        <AccountMenu name={name} email={email} role={role} signOutAction={signOutAction} />
      </div>
    </header>
    <MobileNavDrawer
      nav={mobileNav}
      role={role}
      name={name}
      isStaff={isStaff}
      clientModeEligible={clientModeEligible}
      clientMode={clientMode}
      pendingApprovals={pendingApprovals}
      approvalsHref={approvalsHref}
      triggerRef={mobileMenuTriggerRef}
    />
    </>
  );
}
